import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Car, Clock, Calendar, CheckCircle, Search, RefreshCw,
  MapPin, Loader2, ArrowLeft, Phone, AlertCircle, Camera
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = [
  { key: 'pending', label: 'Booked', description: 'Your booking is confirmed' },
  { key: 'checked_in', label: 'Checked In', description: 'Vehicle received at workshop' },
  { key: 'in_progress', label: 'In Progress', description: 'Service is underway' },
  { key: 'completed', label: 'Completed', description: 'Service finished' },
  { key: 'ready_for_pickup', label: 'Ready', description: 'Ready for collection' }
];

export default function TrackService() {
  const { reference } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [searchRef, setSearchRef] = useState(reference || searchParams.get('ref') || '');
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (reference) {
      fetchTracking(reference);
      
      // Auto-refresh every 30 seconds to get status updates
      const interval = setInterval(() => {
        fetchTracking(reference);
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [reference]);

  const fetchTracking = async (ref) => {
    if (!ref) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/api/track/${encodeURIComponent(ref)}`);
      
      if (response.ok) {
        const data = await response.json();
        setTracking(data);
      } else if (response.status === 404) {
        setError('Booking not found. Please check your reference number.');
        setTracking(null);
      } else {
        setError('Failed to fetch tracking information.');
        setTracking(null);
      }
    } catch (err) {
      console.error('Tracking error:', err);
      setError('Unable to connect. Please try again.');
      setTracking(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchRef.trim()) {
      navigate(`/track/${searchRef.trim()}`);
      fetchTracking(searchRef.trim());
    }
  };

  const getStatusIndex = (status) => {
    const idx = STATUS_STEPS.findIndex(s => s.key === status);
    return idx >= 0 ? idx : 0;
  };

  const currentStatusIndex = tracking ? getStatusIndex(tracking.workshop_status) : -1;

  return (
    <div className="min-h-screen bg-zinc-950 pt-20 pb-12" data-testid="track-service-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#C41E3A]/10 mb-4">
            <Car className="w-8 h-8 text-[#C41E3A]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Track Your Service</h1>
          <p className="text-zinc-400">Enter your booking reference to see real-time updates</p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                value={searchRef}
                onChange={(e) => setSearchRef(e.target.value)}
                placeholder="Enter booking reference (e.g. BK-20260115-ABC123)"
                className="w-full pl-12 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white placeholder-zinc-500 focus:border-[#C41E3A] focus:outline-none"
                data-testid="track-search-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !searchRef.trim()}
              className="px-6 py-3 bg-[#C41E3A] text-white rounded-xl font-medium hover:bg-[#a01830] transition-colors disabled:opacity-50"
              data-testid="track-search-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Track'}
            </button>
          </div>
        </form>

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-900/20 border border-red-900/50 rounded-xl p-4 text-center mb-8"
          >
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-400">{error}</p>
          </motion.div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Looking up your booking...</p>
          </div>
        )}

        {/* Tracking Result */}
        {tracking && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="tracking-result"
          >
            {/* Vehicle & Service Info */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="tracking-info-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-white" data-testid="tracking-vehicle">{tracking.vehicle || 'Your Vehicle'}</h2>
                  <p className="text-zinc-400" data-testid="tracking-service">{tracking.service}</p>
                </div>
                <button
                  onClick={() => fetchTracking(tracking.reference)}
                  className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white"
                  data-testid="tracking-refresh-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Calendar className="w-4 h-4" />
                  <span data-testid="tracking-date">{tracking.scheduled_date}</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Clock className="w-4 h-4" />
                  <span data-testid="tracking-time">{tracking.scheduled_time}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
                <p className="text-xs text-zinc-500" data-testid="tracking-reference">Reference: {tracking.reference}</p>
                {tracking.payment_status === 'paid' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-900/30 text-green-400 text-xs font-medium" data-testid="tracking-paid-badge">
                    <CheckCircle className="w-3 h-3" />
                    Paid
                  </span>
                )}
              </div>
            </div>

            {/* Status Timeline */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-sm font-medium text-zinc-400 mb-6 uppercase">Service Progress</h3>
              
              <div className="space-y-0">
                {STATUS_STEPS.map((step, index) => {
                  const isCompleted = index <= currentStatusIndex;
                  const isCurrent = index === currentStatusIndex;
                  const isLast = index === STATUS_STEPS.length - 1;
                  
                  return (
                    <div key={step.key} className="relative">
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                          isCurrent 
                            ? 'bg-[#C41E3A] ring-4 ring-[#C41E3A]/20' 
                            : isCompleted 
                              ? 'bg-green-600' 
                              : 'bg-zinc-800'
                        }`}>
                          {isCompleted && !isCurrent ? (
                            <CheckCircle className="w-5 h-5 text-white" />
                          ) : (
                            <span className="text-white text-sm font-bold">{index + 1}</span>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className={`flex-1 pb-8 ${isLast ? 'pb-0' : ''}`}>
                          <h4 className={`font-semibold ${isCurrent ? 'text-[#C41E3A]' : isCompleted ? 'text-white' : 'text-zinc-500'}`}>
                            {step.label}
                          </h4>
                          <p className="text-sm text-zinc-500">{step.description}</p>
                          
                          {/* Show timestamp if available */}
                          {tracking.workshop_status_history?.find(h => h.to === step.key) && (
                            <p className="text-xs text-zinc-600 mt-1">
                              {new Date(tracking.workshop_status_history.find(h => h.to === step.key).at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Connector Line */}
                      {!isLast && (
                        <div className={`absolute left-5 top-10 w-0.5 h-8 -translate-x-1/2 ${
                          index < currentStatusIndex ? 'bg-green-600' : 'bg-zinc-800'
                        }`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Photos Section */}
            {tracking.workshop_photos?.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Service Photos
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  {tracking.workshop_photos.map((photo, index) => (
                    <div key={index} className="relative rounded-lg overflow-hidden">
                      <img 
                        src={photo.url} 
                        alt={photo.type} 
                        className="w-full h-32 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                        <p className="text-xs text-white capitalize">{photo.type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Section */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
              <p className="text-zinc-400 mb-4">Questions about your service?</p>
              <a
                href="https://wa.me/27760784050"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-xl font-medium hover:bg-[#128C7E] transition-colors"
              >
                <Phone className="w-5 h-5" />
                Contact Us on WhatsApp
              </a>
            </div>
          </motion.div>
        )}

        {/* No Search Yet */}
        {!tracking && !loading && !error && !reference && (
          <div className="text-center py-12">
            <Car className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 mb-2">Enter your booking reference above</p>
            <p className="text-xs text-zinc-600">You can find it in your confirmation email or SMS</p>
          </div>
        )}

        {/* Back Link */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-zinc-500 hover:text-white text-sm flex items-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
